import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  EnvelopeOpenIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import * as API from '../../api';

/**
 * Email verification — premium redesign (was Material-UI + the legacy banner,
 * which rendered unstyled since the global BEM stylesheet is no longer
 * imported anywhere).
 *
 * Route: /email/verify/:key. Logic preserved verbatim — confirm the key on
 * mount, redirect home after 3s on success, bounce to "/" on 401, and map
 * 422 vs other errors to their respective messages.
 */
export default function EmailVerify() {
  const { key = null } = useParams();
  const history = useHistory();
  const [loading, setLoading] = React.useState(false);
  const [verefyHelper, setVerefyHelper] = React.useState({ type: '', message: '' });

  const sendConfermation = () => {
    setLoading(true);
    API.ConfermVerefecationEmail(key)
      .then((res) => {
        setVerefyHelper({ type: 'success', message: res.message });
        setTimeout(() => {
          history.push('/');
          window.location.reload();
        }, 3000);
      })
      .catch((err) => {
        if (err.status === 401) {
          // /connection?target=login no longer resolves (the route requires a
          // :provider). Sign-in is a modal on the home page now.
          history.push('/');
        }
        if (err.status === 422) {
          setVerefyHelper({ type: 'error', message: err.data.message });
        } else {
          setVerefyHelper({ type: 'error', message: err.data.errors });
        }
      })
      .then(() => setLoading(false));
  };

  React.useEffect(() => {
    if (!key) {
      history.push('/');
    } else {
      sendConfermation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  const ok = verefyHelper.type === 'success';

  return (
    <div className="relative min-h-[70vh] overflow-hidden bg-surface-muted">
      {/* Brand-tinted backdrop, matching the other auth pages */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-50 via-surface-muted to-surface-muted" />
      <div aria-hidden className="pointer-events-none absolute -top-32 -start-24 h-96 w-96 rounded-full bg-brand-500/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -end-24 h-96 w-96 rounded-full bg-accent-500/20 blur-3xl" />

      <div className="relative mx-auto flex w-full max-w-container flex-col items-center px-4 py-12 sm:px-6 sm:py-16">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-white/70 bg-white/90 p-6 text-center shadow-elevated backdrop-blur-sm sm:p-8">

            {loading ? (
              <div className="flex flex-col items-center py-6">
                <span className="inline-block h-10 w-10 animate-spin rounded-full border-[3px] border-brand-500/25 border-t-brand-500" />
                <p className="mt-5 font-display text-lg font-semibold text-ink">
                  Vérification en cours…
                </p>
                <p className="mt-1 text-sm text-ink-muted">Merci de patienter un instant.</p>
              </div>
            ) : null}

            {!loading && verefyHelper.message ? (
              <div className="py-2">
                <span
                  className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl ${
                    ok ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'
                  }`}
                >
                  {ok ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    <ExclamationCircleIcon className="h-6 w-6" />
                  )}
                </span>
                <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-ink">
                  {ok ? 'Adresse vérifiée' : 'Vérification impossible'}
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {verefyHelper.message}
                </p>
                {ok ? (
                  <p className="mt-3 text-xs text-ink-subtle">
                    Vous allez être redirigé automatiquement…
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => history.push('/')}
                    className="mt-5 inline-flex h-11 items-center justify-center rounded-full bg-brand-500 px-6 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
                  >
                    Retour à l'accueil
                  </button>
                )}
              </div>
            ) : null}

            {!loading && !verefyHelper.message ? (
              <div className="py-4">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                  <EnvelopeOpenIcon className="h-6 w-6" />
                </span>
                <h1 className="mt-4 font-display text-xl font-extrabold tracking-tight text-ink">
                  Vérification de votre e-mail
                </h1>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
